/**
 * The controls drawn on top of the image viewer: a frosted pill to group them,
 * and a round icon button to put in it. They replace OpenSeadragon's built-in
 * PNG buttons, so zoom, fullscreen and playback all share one look.
 */
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export function ViewerToolbar({
    className,
    orientation = 'horizontal',
    ...props
}: React.HTMLAttributes<HTMLDivElement> & {
    orientation?: 'horizontal' | 'vertical';
}) {
    return (
        <div
            className={cn(
                'pointer-events-auto flex items-center gap-0.5 p-1 rounded-full',
                'bg-[#1c2620]/70 backdrop-blur-md ring-1 ring-white/10 shadow-lg shadow-black/20',
                orientation === 'vertical' && 'flex-col',
                className,
            )}
            {...props}
        />
    );
}

export const ViewerButton = forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
>(({ className, active, ...props }, ref) => (
    <button
        ref={ref}
        type="button"
        // Keep focus off the button: space and the arrow keys drive playback
        // from a window listener, and a focused button would act on them too.
        onMouseDown={(e) => e.preventDefault()}
        className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-full text-white/85 transition-colors',
            'hover:bg-white/15 hover:text-white',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            'disabled:pointer-events-none disabled:opacity-35',
            '[&_svg]:h-4 [&_svg]:w-4',
            active && 'bg-[#2d6a4f] text-white hover:bg-[#245741]',
            className,
        )}
        {...props}
    />
));
ViewerButton.displayName = 'ViewerButton';
